import React, { useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingBag, Trash2, Plus, Minus, ArrowRight } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToCatalogProducts } from '../utils/firebase';
import type { CatalogProduct } from '../utils/firebase';
import { bookData } from './pages/site/bookData';
function formatPrice(cents: number): string {
  const dollars = (cents / 100).toFixed(2);
  return "$" + dollars;
}
function getCartItemImage(item: {
  productId: string;
  name: string;
  type: string;
}, products: CatalogProduct[]): string | undefined {
  const name = item.name?.trim().toLowerCase();
  if (item.type === "book") {
    const b = products.find(p => p.id === item.productId) ?? products.find(p => p.name.trim().toLowerCase() === name);
    return b?.image;
  }
  const p = products.find(p => p.id === item.productId) ?? products.find(p => p.name.trim().toLowerCase() === name);
  return p?.image;
}
export function CartDrawer() {
  const {
    items,
    subtotal,
    itemCount,
    isOpen,
    closeCart,
    removeItem,
    updateQuantity
  } = useCart();
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = React.useState<CatalogProduct[]>([]);
  useEffect(() => {
    return subscribeToCatalogProducts(setProducts);
  }, []);
  const isShopOrBooks = location.pathname === '/shop' || location.pathname === '/books';
  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  // Close on escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCart();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, closeCart]);
  const handleCheckout = () => {
    closeCart();
    navigate('/checkout-v2');
  };
  return <AnimatePresence data-id="element-58" data-mp="1fi4xai1fdp5e">
      {isOpen && isShopOrBooks && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-charcoal/70 backdrop-blur-sm z-[60]" onClick={closeCart} aria-hidden="true" data-id="element-59" data-mp="1kgyd4h46ps8q" />

          {/* Panel */}
          <motion.aside initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        duration: 0.3,
        ease: [0.2, 0.65, 0.3, 0.9]
      }} className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-[#F9F9F9] z-[70] shadow-2xl flex flex-col" role="dialog" aria-label="Shopping cart" data-id="element-60" data-mp="1z64adzjgp8dz">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E5E5] bg-white" data-id="element-61" data-mp="3eosf5fde7c5a">
              <div className="flex items-center gap-3" data-id="element-62" data-mp="39pz19lu71j4l">
                <h2 className="text-lg font-bold text-[#111111] tracking-tight font-[Avenir,_sans-serif]" data-id="element-63" data-mp="39rjvfaclj01x">
                  Your Cart
                </h2>
                <span className="w-px h-5 bg-[#E5E5E5]" data-id="element-64" data-mp="68gw5m5t2q7v" />
                <span className="font-medium text-[#5A5A5A] text-[18px]" data-id="element-65" data-mp="6b38s07jzvwy">
                  {itemCount} {itemCount === 1 ? 'item' : 'items'}
                </span>
              </div>
              <button onClick={closeCart} className="p-2 text-[#9A9A90] hover:text-tealslate transition-colors" aria-label="Close cart" data-id="element-66" data-mp="3sobat8p0ov0p">
                <X className="w-5 h-5" data-id="element-67" data-mp="p84ifyc1yk78" />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-6 py-6 bg-[#FFFFFF]" data-id="element-68" data-mp="9q39e8pjswdg">
              {items.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center" data-id="element-69" data-mp="dr6jeekwzy6r">
                  <div className="w-16 h-16 bg-white border border-[#E5E5E5] shadow-sm rounded-full flex items-center justify-center mb-5" data-id="element-70" data-mp="3vxb7qr21w2zz">
                    <ShoppingBag className="w-7 h-7 text-[#9A9A90]" data-id="element-71" data-mp="4rjma4z7xeji" />
                  </div>
                  <h3 className="text-xl font-bold text-[#111111] tracking-tight mb-3" data-id="element-72" data-mp="shnn64l0v06t">
                    Your cart is empty.
                  </h3>
                  <p className="text-sm font-medium text-[#5A5A5A] mb-8 max-w-xs leading-relaxed" data-id="element-73" data-mp="iqj68zbsd59c">
                    Browse the bookshop or shop to add items.
                  </p>
                  <div className="flex flex-col gap-3 w-full max-w-xs" data-id="element-74" data-mp="nnjhdonhsy7b">
                    <Link to="/books" onClick={closeCart} className="px-6 py-3 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-2" data-id="element-75" data-mp="1bdm4dqcjvdz2">
                      Browse Books <ArrowRight className="w-4 h-4" data-id="element-76" data-mp="2vx91p6p12zio" />
                    </Link>
                    <Link to="/shop" onClick={closeCart} className="px-6 py-3 bg-white border border-[#E5E5E5] text-[#111111] text-sm font-bold hover:border-tealslate hover:text-tealslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-2" data-id="element-77" data-mp="2vv51ayqc8in5">
                      Shop Supplements <ArrowRight className="w-4 h-4" data-id="element-78" data-mp="2hzkhm12sbweo" />
                    </Link>
                  </div>
                </div> : <ul className="bg-white" data-id="element-79" data-mp="2d0q33bbuou0d">
                  {items.map(item => <li key={item.productId} className="px-4 py-4 border-b border-[#E5E5E5] last:border-b-0 flex gap-3" data-id="element-80" data-mp="2qyxt0zagyv21">
                      {/* Image */}
                      {getCartItemImage(item, products) && <div className="w-20 h-20 shrink-0 flex items-center justify-center" data-id="element-81" data-mp="66m8ks6fr3tr">
                          <img src={getCartItemImage(item, products)} alt="" className="max-w-full max-h-full object-contain" data-id="element-82" data-mp="17sxf6emfowr" />
                        </div>}

                      {/* Details */}
                      <div className="flex-1 min-w-0" data-id="element-83" data-mp="2zyuc75o7qvo4">
                        <p className="text-sm font-bold text-[#111111] leading-snug" data-id="element-84" data-mp="341h6rec9qup0">
                          {item.name}
                        </p>

                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#9A9A90] mt-0.5" data-id="element-85" data-mp="38zaeibt7zwre">
                          {item.type === 'book' ? 'Digital Book' : 'Supplement'}
                        </p>

                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-3" data-id="element-86" data-mp="2gc61pknvye52">
                          <div className="flex items-center border border-[#E5E5E5] rounded-md overflow-hidden" data-id="element-87" data-mp="2l9fxrj5s5o1v">
                            <button onClick={() => {
                      if (item.quantity === 1) {
                        removeItem(item.productId);
                      } else {
                        updateQuantity(item.productId, item.quantity - 1);
                      }
                    }} className={`px-2.5 py-1.5 transition-colors ${item.quantity === 1 ? 'text-[#5A5A5A] hover:bg-burgundy/10 hover:text-burgundy' : 'text-[#5A5A5A] hover:bg-[#F9F9F9]'}`} aria-label={item.quantity === 1 ? 'Remove item' : 'Decrease quantity'} data-id="element-88" data-mp="2q3snuc1rhxml">
                              {item.quantity === 1 ? <Trash2 className="w-3 h-3" data-id="element-89" data-mp="1c1crke0e0a2q" /> : <Minus className="w-3 h-3" data-id="element-90" data-mp="1c47dyb2yo4ys" />}
                            </button>
                            <span className="px-3 text-sm font-bold text-[#111111] min-w-[1.75rem] text-center" data-id="element-91" data-mp="1754hvg4yl92y">
                              {item.quantity}
                            </span>
                            <button onClick={() => updateQuantity(item.productId, item.quantity + 1)} className="px-2.5 py-1.5 text-[#5A5A5A] hover:bg-[#F9F9F9] transition-colors" aria-label="Increase quantity" data-id="element-92" data-mp="xb5homog3zdn">
                              <Plus className="w-3 h-3" data-id="element-93" data-mp="oecxljjfny9s" />
                            </button>
                          </div>
                          <button type="button" onClick={() => removeItem(item.productId)} className="cart-delete-button text-sm font-medium text-[#2D2D2D] hover:text-burgundy transition-colors inline-flex items-center gap-1" data-id="element-94" data-mp="279n40728tbjc">
                            <Trash2 className="w-3.5 h-3.5" data-id="element-95" data-mp="1y8f9m02ukuuj" />
                            Delete
                          </button>
                        </div>
                      </div>

                      {/* Price */}
                      <div className="shrink-0 text-right" data-id="element-96" data-mp="1oiche23jvkx9">
                        <p className="text-sm font-bold text-[#111111]" data-id="element-97" data-mp="1tand0sr9ckk0">
                          {formatPrice(item.unitPrice * item.quantity)}
                        </p>
                      </div>
                    </li>)}
                </ul>}
            </div>

            {/* Footer */}
            {items.length > 0 && <div className="px-6 py-6 space-y-4 bg-[#FBFBFB]" data-id="element-98" data-mp="15lugau2ta8oc">
                <div className="text-right" data-id="element-99" data-mp="1ajmjwlctjj8d">
                  <p className="text-base text-[#111111]" data-id="element-100" data-mp="1ajcr6q0e2xyz">
                    Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'}):{' '}
                    <span className="font-bold" data-id="element-101" data-mp="lyzod9ugu4yt">{formatPrice(subtotal)}</span>
                  </p>
                </div>
                <p className="text-sm text-[#5A5A5A]" data-id="element-102" data-mp="zzagshbfbrm3">
                  Shipping and taxes calculated at checkout.
                </p>
                <button onClick={handleCheckout} className="w-full px-8 py-4 bg-tealslate text-white font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-3 text-[18px]" data-id="element-103" data-mp="vvm79t38ely7">
                  Checkout 
                </button>
              </div>}
          </motion.aside>
        </>}
    </AnimatePresence>;
}